/**
 * Admin Protected Route
 * Only allows access to users with admin privileges
 */

import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { apiGet } from '../utils/api';
import { storage } from '../services/storage';

interface AdminUser {
  id: string;
  email: string;
  is_admin: boolean;
}

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

// Enhanced authentication configuration for Firebase portal
// Fixed: Ensure admin auth endpoint is used (/api/admin/auth/me)
const AUTH_CONFIG = {
  tokenRefresh: true,
  errorHandling: "comprehensive",
  sessionManagement: "robust",
  retryAttempts: 3,
  sessionTimeout: 30 * 60 * 1000, // 30 minutes
  tokenRefreshThreshold: 5 * 60 * 1000 // 5 minutes before expiry
};

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    checkAdminAuth();
  }, []); // Only run once on mount

  useEffect(() => {
    // Set up session timeout monitoring only when we have an authenticated admin user
    if (!adminUser) return;
    
    const sessionCheck = setInterval(() => {
      checkSessionValidity();
    }, AUTH_CONFIG.sessionTimeout / 6); // Check every 5 minutes
    
    return () => clearInterval(sessionCheck);
  }, [adminUser]);

  const checkSessionValidity = async () => {
    try {
      const adminToken = await storage.getItem('admin_token');
      if (!adminToken) {
        console.log('[AdminProtectedRoute] No admin token found');
        setAdminUser(null);
        setError('Session expired - please log in again');
        return;
      }
      
      const response = await apiGet('/api/admin/auth/me', {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (!response.ok) {
        console.log('[AdminProtectedRoute] Session expired, redirecting to login');
        setAdminUser(null);
        setError('Session expired - please log in again');
      }
    } catch (err) {
      console.warn('[AdminProtectedRoute] Session check failed:', err);
    }
  };

  const checkAdminAuth = async (attempt: number = 0) => {
    try {
      console.log(`[AdminProtectedRoute] Starting auth check (attempt ${attempt + 1})...`);
      
      // Get admin token from localStorage
      const adminToken = await storage.getItem('admin_token');
      if (!adminToken) {
        console.log('[AdminProtectedRoute] No admin token found in localStorage');
        setError('Authentication required - please log in');
        setLoading(false);
        return;
      }
      
      // Enhanced error handling with retry logic
      // Use admin auth endpoint, not user auth endpoint
      const response = await apiGet('/api/admin/auth/me', {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      console.log('[AdminProtectedRoute] Response received:', response.status, response.headers.get('content-type'));

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        
        // Robust JSON response validation
        if (contentType && contentType.includes('application/json')) {
          const responseData = await response.json();
          console.log('[AdminProtectedRoute] Response data:', responseData);
          
          // Admin endpoint returns { success: true, admin: { ... } }
          if (responseData.success && responseData.admin) {
            const adminData = responseData.admin;
            setAdminUser({
              id: adminData.admin_id,
              email: adminData.email,
              is_admin: true
            });
            setError(null);
            setRetryCount(0);
            
            // Log successful admin authentication for security audit
            console.log('[AdminProtectedRoute] Admin authentication successful for:', adminData.email);
          } else {
            setError('Admin access required - insufficient privileges');
            console.warn('[AdminProtectedRoute] Invalid admin response structure:', responseData);
          }
        } else {
          // Enhanced error handling for non-JSON responses
          console.error('Admin auth check failed: Server returned non-JSON response');
          console.error('Content-Type:', contentType);
          
          const textResponse = await response.text();
          console.error('Response body (first 500 chars):', textResponse.substring(0, 500));
          
          // Provide more specific error messages
          if (textResponse.includes('502') || textResponse.includes('503')) {
            setError('Backend service temporarily unavailable - please try again in a moment');
          } else if (textResponse.includes('CORS')) {
            setError('Cross-origin authentication issue - please contact support');
          } else {
            setError('Authentication service returned unexpected response');
          }
        }
      } else {
        console.log('[AdminProtectedRoute] Non-OK response:', response.status);
        
        // Enhanced status code handling
        switch (response.status) {
          case 401:
            setError('Authentication required - please log in');
            break;
          case 403:
            setError('Access forbidden - admin privileges required');
            break;
          case 404:
            setError('Authentication endpoint not found - service may be down');
            break;
          case 500:
            setError('Server error - please try again later');
            break;
          case 502:
          case 503:
          case 504:
            setError('Service temporarily unavailable - please try again');
            break;
          default:
            setError(`Authentication failed (HTTP ${response.status})`);
        }
        
        // Retry logic for temporary failures
        if ((response.status >= 500 || response.status === 0) && attempt < AUTH_CONFIG.retryAttempts) {
          console.log(`[AdminProtectedRoute] Retrying auth check in 2 seconds...`);
          setTimeout(() => {
            setRetryCount(attempt + 1);
            checkAdminAuth(attempt + 1);
          }, 2000 * (attempt + 1)); // Exponential backoff
          return; // Don't set loading to false yet
        }
      }
    } catch (err) {
      console.error('Admin auth check failed:', err);
      
      // Enhanced error categorization
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Network error - unable to reach authentication server');
      } else if (err instanceof SyntaxError && err.message.includes('JSON')) {
        setError('Invalid response format from authentication server');
      } else {
        setError('Authentication check failed - please try again');
      }
      
      // Retry logic for network errors
      if (attempt < AUTH_CONFIG.retryAttempts) {
        console.log(`[AdminProtectedRoute] Retrying due to error in 3 seconds...`);
        setTimeout(() => {
          setRetryCount(attempt + 1);
          checkAdminAuth(attempt + 1);
        }, 3000 * (attempt + 1)); // Exponential backoff
        return; // Don't set loading to false yet
      }
    } finally {
      // Only set loading to false if we're not retrying
      if (attempt >= AUTH_CONFIG.retryAttempts) {
        setLoading(false);
      } else if (!error) {
        setLoading(false);
      }
    }
  };

  // Enhanced loading state with retry information
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">Verifying admin access...</p>
          {retryCount > 0 && (
            <p className="text-sm text-gray-500">
              Retry attempt {retryCount} of {AUTH_CONFIG.retryAttempts}
            </p>
          )}
          <div className="mt-4 text-xs text-gray-400">
            <p>🔒 Secure authentication in progress</p>
            <p>🌐 Cross-origin session validation</p>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced error display with actionable guidance
  if (!adminUser || error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-md">
          <div className="mb-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
            <p className="text-gray-600 mb-4">{error || 'Admin access verification failed'}</p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => {
                setLoading(true);
                setError(null);
                setRetryCount(0);
                checkAdminAuth();
              }}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Retry Authentication
            </button>
            
            <Navigate to="/admin/login" replace />
          </div>
          
          <div className="mt-6 text-xs text-gray-400 space-y-1">
            <p>🔐 Secure Firebase Admin Portal</p>
            <p>🌐 Cross-origin authentication enabled</p>
            <p>⚡ Enhanced error handling active</p>
          </div>
        </div>
      </div>
    );
  }

  // Log successful admin access for security audit
  console.log('[AdminProtectedRoute] Rendering admin content for:', adminUser.email);

  // Render protected content WITHOUT AdminLayout wrapper
  // (pages now handle their own layout)
  return <>{children}</>;
};

export default AdminProtectedRoute; 